// Single-line auto-rotating tip strip. Lives in the bottom of the GapLedger
// column — the rightmost panel — where it doesn't compete for space with
// any active interaction. Pauses on hover so the user can read; rotates
// every 8s otherwise. `×` permanently dismisses to localStorage; restored
// only by clearing that key.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Educational tips. Each one teaches ONE concrete thing the user can act
// on, fits one line at typical viewport width, and isn't already obvious
// from surrounding UI. Append to the end so existing users see fresh
// content rotate in over time. Stored as i18n keys; resolved at render
// time so a locale switch updates the visible tip immediately.
const TIP_KEYS: readonly string[] = [
  'tips.newline',
  'tips.dragFile',
  'tips.projectRefs',
  'tips.exportProject',
  'tips.importZip',
  'tips.renameDoubleClick',
  'tips.editHistory',
  'tips.settings',
  'tips.cardShortcuts',
];
const DISMISSED_KEY = 'draftlize.tipsDismissed';
const ROTATE_MS = 8000;

interface Props {
  /** When true, the rotation timer pauses but the visible tip stays. Used
   *  by callers that want to freeze the strip during high-attention moments
   *  (typing, hovering an adjacent control, etc.). The component also
   *  internally pauses on its own hover so the user can read end-to-end. */
  paused?: boolean;
}

export function TipsTicker({ paused = false }: Props) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  // Random start so two open tabs don't show the same tip in lockstep.
  const [idx, setIdx] = useState<number>(() =>
    Math.floor(Math.random() * TIP_KEYS.length),
  );
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (dismissed || paused || hover) return;
    const id = window.setInterval(() => {
      setIdx((i) => (i + 1) % TIP_KEYS.length);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [dismissed, paused, hover]);

  if (dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      /* private mode — silently degrade */
    }
  };
  const next = () => setIdx((i) => (i + 1) % TIP_KEYS.length);
  const tipText = t(TIP_KEYS[idx]);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="mono"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 10,
        color: 'var(--ink-3)',
        minWidth: 0,
        width: '100%',
      }}
    >
      <span style={{ flexShrink: 0, opacity: 0.7 }}>💡</span>
      <span
        title={tipText}
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
          flex: 1,
        }}
      >
        {tipText}
      </span>
      {hover && (
        <>
          <button
            onClick={next}
            title={t('tips.nextTip')}
            style={{
              fontSize: 12,
              lineHeight: 1,
              color: 'var(--ink-3)',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '0 2px',
              flexShrink: 0,
            }}
          >
            ›
          </button>
          <button
            onClick={dismiss}
            title={t('tips.hideTips')}
            style={{
              fontSize: 11,
              lineHeight: 1,
              color: 'var(--ink-3)',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: '0 2px',
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </>
      )}
    </div>
  );
}
